import {
  GoogleSearchConsoleAccount,
  GoogleSearchConsoleSite,
  parseGoogleSearchConsoleAccount,
} from "@/schemas/firestore/googleSearchConsole.schema";
import { StateCreator } from "zustand";
import { StoreState } from "..";

interface GoogleSearchConsoleUIState {
  isLoadingAccounts: boolean;
  isConnecting: boolean;
  isLoadingStatus: Record<string, boolean>;
  isLoadingSites: Record<string, boolean>;
  isDisconnecting: Record<string, boolean>;
}

export interface GoogleSearchConsoleSlice {
  /* state */
  gscAccounts: GoogleSearchConsoleAccount[];
  gscUiState: GoogleSearchConsoleUIState;
  gscErrors: Record<string, string>;

  /* actions */
  setGscAccounts: (accounts: GoogleSearchConsoleAccount[]) => void;
  addGscAccount: (account: GoogleSearchConsoleAccount) => void;
  updateGscAccount: (
    accountId: string,
    updates: Partial<GoogleSearchConsoleAccount>,
  ) => void;
  removeGscAccount: (accountId: string) => void;
  setGscUIState: (updates: Partial<GoogleSearchConsoleUIState>) => void;
  setGscErrorForAccount: (accountId: string, error: string | null) => void;
  clearGscErrorForAccount: (accountId: string) => void;
  resetGscState: () => void;

  /* getters */
  getGscAccountById: (
    accountId: string,
  ) => GoogleSearchConsoleAccount | null;
  getSitesForGscAccount: (accountId: string) => GoogleSearchConsoleSite[];
  hasConnectedGscAccounts: () => boolean;
}

const initialGscUiState: GoogleSearchConsoleUIState = {
  isLoadingAccounts: false,
  isConnecting: false,
  isLoadingStatus: {},
  isLoadingSites: {},
  isDisconnecting: {},
};

export const createGoogleSearchConsoleSlice: StateCreator<
  StoreState,
  [],
  [],
  GoogleSearchConsoleSlice
> = (set, get) => ({
  gscAccounts: [],
  gscUiState: initialGscUiState,
  gscErrors: {},

  setGscAccounts: (accounts) => {
    const validatedAccounts = accounts.map((account, index) => {
      try {
        return parseGoogleSearchConsoleAccount(account);
      } catch (error) {
        console.error(
          `[googleSearchConsoleSlice] Validation error for account at index ${index}:`,
          error instanceof Error ? error.message : "Unknown error",
          "Raw data:",
          account,
        );
        throw error;
      }
    });
    set({ gscAccounts: validatedAccounts });
  },

  addGscAccount: (account) => {
    try {
      const validatedAccount = parseGoogleSearchConsoleAccount(account);
      set((state) => ({
        gscAccounts: [
          ...state.gscAccounts.filter(
            (existing) =>
              existing.account_id !== validatedAccount.account_id,
          ),
          validatedAccount,
        ],
      }));
    } catch (error) {
      console.error(
        `[googleSearchConsoleSlice] Validation error for account ${account.account_id}:`,
        error instanceof Error ? error.message : "Unknown error",
        "Raw data:",
        account,
      );
      throw error;
    }
  },

  updateGscAccount: (accountId, updates) => {
    set((state) => ({
      gscAccounts: state.gscAccounts.map((account) => {
        if (account.account_id !== accountId) return account;
        return parseGoogleSearchConsoleAccount({ ...account, ...updates });
      }),
    }));
  },

  removeGscAccount: (accountId) => {
    set((state) => {
      const gscErrors = { ...state.gscErrors };
      delete gscErrors[accountId];
      return {
        gscAccounts: state.gscAccounts.filter(
          (account) => account.account_id !== accountId,
        ),
        gscErrors,
      };
    });
  },

  setGscUIState: (updates) => {
    set((state) => ({
      gscUiState: { ...state.gscUiState, ...updates },
    }));
  },

  setGscErrorForAccount: (accountId, error) => {
    set((state) => {
      if (error === null) {
        const gscErrors = { ...state.gscErrors };
        delete gscErrors[accountId];
        return { gscErrors };
      }
      return {
        gscErrors: { ...state.gscErrors, [accountId]: error },
      };
    });
  },

  clearGscErrorForAccount: (accountId) => {
    set((state) => {
      const gscErrors = { ...state.gscErrors };
      delete gscErrors[accountId];
      return { gscErrors };
    });
  },

  resetGscState: () => {
    set({
      gscAccounts: [],
      gscUiState: initialGscUiState,
      gscErrors: {},
    });
  },

  getGscAccountById: (accountId) => {
    return (
      get().gscAccounts.find((account) => account.account_id === accountId) ||
      null
    );
  },

  getSitesForGscAccount: (accountId) => {
    return get().getGscAccountById(accountId)?.sites || [];
  },

  hasConnectedGscAccounts: () => {
    return get().gscAccounts.some((account) => account.status === "connected");
  },
});
